import { Input } from "@/components/inputs/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  SelectContent,
  SelectTrigger,
  SelectValue,
  Select,
  SelectItem,
} from "@/components/ui/select";
import {
  Form,
  useActionData,
  useLoaderData,
  useNavigate,
  useNavigation,
} from "react-router-dom";
import { parseWithZod } from "@conform-to/zod";
import { Action, schema } from "./action";
import { getInputProps, useForm } from "@conform-to/react";
import { NumberInput } from "@/components/inputs/number-input/number-input";
import { LoaderData } from "./loader";
import { DEFAULT_ROUTES } from "@/constants/routes";
import { MoneyInput } from "@/components/inputs/money-input/money-input";
import { ComboboxDemo } from "@/components/ui/combobox";
import { debounce } from "@/utils/debounce";
import { useCompanySelection } from "@/hooks/useCompanySelection";
import { usePackageSelection } from "@/hooks/usePackageSelection";
import { PageNavigationHeader } from "@/features/license-fee/list/layouts/page-navigation-header";
import {
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
} from "@/components/ui/table";
import { formatCurrency } from "@/utils/format/currency";
import { useEffect, useState, useCallback } from "react";
import {
  differenceInYears,
  getYear,
  addYears,
  addMonths,
  parseISO,
} from "date-fns";
import { MonthPicker } from "@/components/inputs/month-input/month-picker";
import { formatInTimeZone } from "date-fns-tz";
import { useCalculateInvoicingCompetency } from "@/hooks/useCalculateInvoicingCompetency";
import { parseMoneyValue } from "@/utils/parse-money";

export const addMonthsToDate = (date: string, months: number) => {
  return formatInTimeZone(
    addMonths(parseISO(date), months),
    "UTC",
    "yyyy-MM-dd",
  );
};

export default function LicenseFeeCreate() {
  const actionData = useActionData() as Action;
  const { tierOptions, paymentMethodOptions, providerOptions } =
    useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const navigation = useNavigation();

  const isSubmitting = navigation.state === "submitting";

  const [form, fields] = useForm({
    defaultValue: actionData?.values ?? {},
    lastResult: actionData?.submission,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema });
    },
    shouldValidate: "onBlur",
    shouldRevalidate: "onInput",
  });

  const {
    companies,
    companyPackages,
    handleSearchCompanies,
    handleCompanySelect,
  } = useCompanySelection({});
  const { filteredPackages, handleSearchPackages, setPackages } =
    usePackageSelection({ defaultPackages: companyPackages });

  const { calculateInvoicingCompetency } = useCalculateInvoicingCompetency();

  const [installmentsData, setInstallmentsData] = useState<
    Array<{
      id: number;
      date: any;
      amount: any;
      type: "setup-fee" | "license-fee";
      competence: string | null;
      invoiceCompetence: any;
    }>
  >([]);

  const [firstPaymentDate, setFirstPaymentDate] = useState<string | null>(null);

  const getPaymentCompetence = (
    paymentDate: string,
    firstPaymentDate: string,
  ) => {
    const licenseFeeStartDate = fields.startDate.value || "";
    const licenseFeeEndDate = fields.endDate.value || "";

    const licenseFeeDuration = differenceInYears(
      licenseFeeEndDate,
      licenseFeeStartDate,
    );

    if (licenseFeeDuration > 0) {
      const diffYears = differenceInYears(paymentDate, firstPaymentDate);
      const competenceDate = parseISO(licenseFeeStartDate);
      const adjustedDate = addYears(competenceDate, diffYears);

      return `${getYear(adjustedDate)}-01`;
    }

    return formatInTimeZone(paymentDate, "UTC", "yyyy-MM");
  };

  const calculateInstallments = useCallback(async () => {
    const setupFee = fields.setupFee.value || 0;
    const totalAmount = fields.totalAmount.value || 0;
    const installments = Math.max(0, Number(fields.installments.value) || 0);

    if (
      !installments ||
      !fields.startDate.value ||
      !fields.exceededPaymentDeadline.value
    ) {
      setInstallmentsData([]);
      return;
    }

    const parsedTotalAmount = parseMoneyValue(totalAmount as string);

    if (parsedTotalAmount > 0 && installments > 0) {
      const baseInstallmentValue = Number(
        (parsedTotalAmount / installments).toFixed(2),
      );
      const firstInstallmentValue = Number(
        (parsedTotalAmount - baseInstallmentValue * (installments - 1)).toFixed(
          2,
        ),
      );

      const newInstallments: {
        id: number;
        date: string | null;
        amount: number;
        type: "setup-fee" | "license-fee";
        competence: string | null;
        invoiceCompetence: string | null;
      }[] = [];

      if (
        (typeof setupFee === "number" && setupFee > 0) ||
        (typeof setupFee === "string" && setupFee !== "0,00")
      ) {
        const parsedSetupFee = parseMoneyValue(setupFee);
        const setupFeeCompetence = getPaymentCompetence(
          fields.startDate.value || "",
          fields.startDate.value || "",
        );
        newInstallments.push({
          id: 0,
          date: fields.startDate.value || null,
          amount: parsedSetupFee,
          type: "setup-fee" as const,
          competence: setupFeeCompetence,
          invoiceCompetence: null,
        });
      }

      const licenseFeeItems = Array.from(
        { length: installments },
        (_, index) => ({
          id: index + 1,
          date: firstPaymentDate
            ? addMonthsToDate(firstPaymentDate, index)
            : null,
          amount: index === 0 ? firstInstallmentValue : baseInstallmentValue,
          type: "license-fee" as const,
          competence: firstPaymentDate
            ? getPaymentCompetence(
                addMonthsToDate(firstPaymentDate || "", index),
                firstPaymentDate || "",
              )
            : null,
          invoiceCompetence: null,
        }),
      );

      newInstallments.push(...licenseFeeItems);

      const exceededDeadline = Number(
        fields.exceededPaymentDeadline.value || 0,
      );
      const datesList = newInstallments
        .map((item) => item.date)
        .filter(Boolean) as string[];

      if (datesList.length > 0) {
        const invoicingDates = await calculateInvoicingCompetency(
          datesList,
          exceededDeadline,
        );

        let respIdx = 0;
        const updatedWithInvoicing = newInstallments.map((item) => {
          if (item.date) {
            const resp = invoicingDates[respIdx];
            respIdx += 1;
            return {
              ...item,
              invoiceCompetence:
                resp?.invoicingCompetencyDate || item.invoiceCompetence,
            };
          }
          return item;
        });

        setInstallmentsData((prevState) => {
          if (
            JSON.stringify(prevState) === JSON.stringify(updatedWithInvoicing)
          ) {
            return prevState;
          }
          return updatedWithInvoicing;
        });
      } else {
        setInstallmentsData((prevState) => {
          if (JSON.stringify(prevState) === JSON.stringify(newInstallments)) {
            return prevState;
          }
          return newInstallments;
        });
      }
    }
  }, [
    fields.setupFee.value,
    fields.totalAmount.value,
    fields.installments.value,
    fields.startDate.value,
    fields.endDate.value,
    fields.exceededPaymentDeadline.value,
    firstPaymentDate,
  ]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculateInstallments();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [calculateInstallments]);

  useEffect(() => {
    setPackages(companyPackages);
  }, [companyPackages]);

  return (
    <div className="bg-slate-50">
      <div className="p-6 max-w-4xl mx-auto bg-slate-50 h-full gap-8 flex flex-col">
        <PageNavigationHeader
          onClick={() => navigate(DEFAULT_ROUTES.LICENSE_FEE_LIST)}
          title="Criando registro de license fee"
          subtitle={
            <>
              Associe um license fee à uma empresa contrante.{" "}
              <span className="font-medium">
                Campos com asterísco * são obrigatórios.
              </span>
            </>
          }
        />

        <Form
          className="gap-8 flex flex-col"
          method="POST"
          id={form.id}
          onSubmit={form.onSubmit}
        >
          <div className="flex-col justify-start items-start gap-8 inline-flex">
            <div className="self-stretch flex-col justify-start items-start gap-px flex">
              <div className="self-stretch text-black text-lg font-medium font-['Inter'] leading-7">
                Empresa
              </div>
              <div className="self-stretch text-zinc-500 text-sm font-normal font-['Inter'] leading-tight">
                Defina que companhia será associada a este license fee.
              </div>
            </div>

            <div className="self-stretch justify-start items-start gap-4 inline-flex">
              <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                <ComboboxDemo
                  key={fields.companyId.key}
                  name={fields.companyId.name}
                  emptyPlaceholder="Empresa não encontrada"
                  searchPlaceholder="Busque uma empresa"
                  selectPlaceholder="Busque por nome *"
                  onChange={debounce(handleSearchCompanies, 500)}
                  items={companies}
                  onSelect={(item) => handleCompanySelect({ companyId: item })}
                  shouldFilter={false}
                  minSearchLength={3}
                />
                <p className="text-red-500 text-sm">
                  {fields.companyId.errors}
                </p>
              </div>
              <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                <ComboboxDemo
                  key={fields.packageId.key}
                  name={fields.packageId.name}
                  emptyPlaceholder="Pacote não encontrado"
                  searchPlaceholder="Busque um pacote"
                  selectPlaceholder="Pacote *"
                  onChange={debounce(handleSearchPackages, 500)}
                  items={filteredPackages}
                />
                <p className="text-red-500 text-sm">
                  {fields.packageId.errors}
                </p>
              </div>
            </div>

            <div className="self-stretch flex-col justify-start items-start gap-2.5 flex">
              <div className="self-stretch h-px border border-[#e3e3e7]" />
            </div>

            <div className="self-stretch flex-col justify-start items-start gap-px flex">
              <div className="self-stretch text-black text-lg font-medium font-['Inter'] leading-7">
                Configurações
              </div>
              <div className="self-stretch text-zinc-500 text-sm font-normal font-['Inter'] leading-tight">
                Defina os principais parâmetros deste license fee.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 w-full">
              <Input
                label="Data de início *"
                className="col-span-1"
                showlabel
                {...getInputProps(fields.startDate, { type: "date" })}
                error={fields.startDate.errors}
              />
              <Input
                label="Data de fim *"
                className="col-span-1"
                showlabel
                {...getInputProps(fields.endDate, { type: "date" })}
                error={fields.endDate.errors}
              />

              <div>
                <Label>Tier</Label>
                <Select
                  key={fields.tier.key}
                  name={fields.tier.name}
                  defaultValue={fields.tier.value as string}
                >
                  <SelectTrigger className="mt-1 w-full h-10 px-3 py-2 text-sm border rounded-md bg-white border-[#d4d4d8] ring-inset">
                    <SelectValue placeholder="Selecione um tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {tierOptions.map((tier) => (
                      <SelectItem key={tier.value} value={tier.value}>
                        {tier.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <NumberInput
                label="Membros"
                showButtons={false}
                {...getInputProps(fields.members, { type: "number" })}
                error={fields.members.errors}
              />

              <div className="col-span-2">
                <Label>Notas</Label>
                <Textarea
                  placeholder="Anote detalhes importantes sobre este license fee"
                  className="h-32 bg-white border-[#d4d4d8] ring-inset"
                  {...getInputProps(fields.notes, { type: "text" })}
                />
              </div>
            </div>

            <div className="self-stretch flex-col justify-start items-start gap-2.5 flex">
              <div className="self-stretch h-px border border-[#e3e3e7]" />
            </div>

            <div className="self-stretch flex-col justify-start items-start gap-px flex">
              <div className="self-stretch text-black text-lg font-medium font-['Inter'] leading-7">
                Pagamento
              </div>
              <div className="self-stretch text-zinc-500 text-sm font-normal font-['Inter'] leading-tight">
                Defina os detalhes de pagamento para este license fee.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 w-full">
              <div>
                <Label>Valor total em R$ *</Label>
                <MoneyInput
                  label="Valor total em R$ *"
                  placeholder="Digite o valor total"
                  {...getInputProps(fields.totalAmount, { type: "text" })}
                  error={fields.totalAmount.errors}
                />
              </div>
              <div>
                <Label>Isenção total em R$ *</Label>
                <MoneyInput
                  label="Isenção total em R$ *"
                  placeholder="Digite o valor da isenção"
                  {...getInputProps(fields.exemptionAmount, { type: "text" })}
                  error={fields.exemptionAmount.errors}
                />
              </div>
              <div>
                <NumberInput
                  label="Parcelas *"
                  placeholder="Digite o número de parcelas"
                  showButtons={false}
                  {...getInputProps(fields.installments, { type: "number" })}
                  error={fields.installments.errors}
                />
              </div>
              <div>
                <NumberInput
                  label="Prazo para crédito excedentes *"
                  placeholder="Digite o número em dias do prazo para excedentes"
                  showButtons={false}
                  {...getInputProps(fields.exceededPaymentDeadline, {
                    type: "number",
                  })}
                  error={fields.exceededPaymentDeadline.errors}
                />
              </div>
              <div>
                <Label>Provedor *</Label>
                <Select
                  key={fields.providerId.key}
                  name={fields.providerId.name}
                  defaultValue={fields.providerId.value as string}
                >
                  <SelectTrigger className="mt-1 w-full h-10 px-3 py-2 text-sm border rounded-md bg-white border-[#d4d4d8] ring-inset">
                    <SelectValue placeholder="Selecione um provedor" />
                  </SelectTrigger>
                  <SelectContent>
                    {providerOptions.map((provider) => (
                      <SelectItem
                        key={provider.value}
                        value={provider.value.toString()}
                      >
                        {provider.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-red-500 text-sm">
                  {fields.providerId.errors}
                </p>
              </div>
              <div>
                <Label>Método *</Label>
                <Select
                  key={fields.paymentMethodId.key}
                  name={fields.paymentMethodId.name}
                  defaultValue={fields.paymentMethodId.value as string}
                >
                  <SelectTrigger className="mt-1 w-full h-10 px-3 py-2 text-sm border rounded-md bg-white border-[#d4d4d8] ring-inset">
                    <SelectValue
                      placeholder="Selecione um método"
                      className="text-zinc-500"
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethodOptions.map((paymentMethod) => (
                      <SelectItem
                        key={paymentMethod.value}
                        value={paymentMethod.value.toString()}
                      >
                        {paymentMethod.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-red-500 text-sm">
                  {fields.paymentMethodId.errors}
                </p>
              </div>
              <div>
                <Label>Setup fee</Label>
                <MoneyInput
                  label="Setup fee"
                  placeholder="Digite o valor do setup fee"
                  {...getInputProps(fields.setupFee, { type: "text" })}
                  error={fields.setupFee.errors}
                />
              </div>
            </div>

            {Number(fields.installments.value) > 0 &&
              !!fields.startDate.value &&
              !!fields.endDate.value &&
              !!fields.exceededPaymentDeadline.value &&
              parseMoneyValue(fields.totalAmount.value as string) > 0 && (
                <>
                  <div className="self-stretch flex-col justify-start items-start gap-px flex">
                    <div className="self-stretch text-black text-lg font-medium font-['Inter'] leading-7">
                      Cobranças
                    </div>
                    <div className="self-stretch text-zinc-500 text-sm font-normal font-['Inter'] leading-tight">
                      Informe as datas e revise as cobranças deste license fee.
                    </div>
                  </div>

                  <div className="block w-full bg-white border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                    <Table className="border-collapse">
                      <TableHeader className="h-[52px]">
                        <TableRow>
                          <TableHead className="px-4 text-zinc-500 border-r border-zinc-200">
                            #
                          </TableHead>
                          <TableHead className="px-6 text-zinc-500 border-r border-zinc-200">
                            Data *
                          </TableHead>
                          <TableHead className="px-6 text-zinc-500 border-r border-zinc-200">
                            Competência do pagamento
                          </TableHead>
                          <TableHead className="px-6 text-zinc-500 border-r border-zinc-200">
                            Competência de faturamento
                          </TableHead>
                          <TableHead className="px-6 text-zinc-500">
                            Valor em R$
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {installmentsData.map((installment) => (
                          <TableRow className="h-[52px]" key={installment.id}>
                            <TableCell className="px-4 border-r border-zinc-200">
                              {installment.id}
                            </TableCell>
                            <TableCell className="border-r border-zinc-200 p-2">
                              <Input
                                label="Data"
                                className="w-full h-full border-none ring-0 shadow-none focus:ring-0 focus:border-none"
                                value={installment.date}
                                alternativeCalendarIcon
                                onChange={(e) => {
                                  const newDate = e.target.value;
                                  if (installment.id === 1) {
                                    setFirstPaymentDate(newDate);
                                  }
                                  const newCompetence = getPaymentCompetence(
                                    newDate,
                                    fields.startDate.value || "",
                                  );
                                  const updatedInstallments =
                                    installmentsData.map((payment) =>
                                      payment.id === installment.id
                                        ? {
                                            ...payment,
                                            date: newDate,
                                            competence: newCompetence,
                                          }
                                        : payment,
                                    );
                                  setInstallmentsData(updatedInstallments);
                                }}
                                {...getInputProps(fields.installmentsDates, {
                                  type: "date",
                                })}
                              />
                            </TableCell>
                            <TableCell className="border-r border-zinc-200 p-2">
                              <MonthPicker
                                className="w-full h-full border-none ring-0 shadow-none focus:ring-0 focus:border-none"
                                value={installment.competence || ""}
                                onChange={(value) => {
                                  const formField = (fields as any)[
                                    `installmentsCompetence[${installment.id}]`
                                  ];
                                  if (formField) {
                                    formField.value = value;
                                  }
                                }}
                                {...getInputProps(
                                  fields.installmentsCompetence,
                                  { type: "text" },
                                )}
                              />
                            </TableCell>
                            <TableCell className="border-r border-zinc-200 p-2">
                              <Input
                                label="Faturamento"
                                className="w-full h-full border-none ring-0 shadow-none focus:ring-0 focus:border-none"
                                value={installment.invoiceCompetence || ""}
                                alternativeCalendarIcon
                                onChange={(e) => {
                                  const newDate = e.target.value;
                                  setInstallmentsData((prev) =>
                                    prev.map((payment) =>
                                      payment.id === installment.id
                                        ? {
                                            ...payment,
                                            invoiceCompetence: newDate,
                                          }
                                        : payment,
                                    ),
                                  );
                                }}
                                {...getInputProps(
                                  fields.installmentsInvoiceCompetence,
                                  {
                                    type: "date",
                                  },
                                )}
                              />
                            </TableCell>
                            <TableCell className="p-2">
                              {installment.type === "license-fee" ? (
                                <div className="relative">
                                  <MoneyInput
                                    label="Valor"
                                    defaultValue={installment.amount}
                                    {...getInputProps(
                                      fields.installmentsAmounts,
                                      { type: "text" },
                                    )}
                                    className="w-full h-full border-none ring-0 shadow-none focus:ring-0 focus:border-none pl-8"
                                  />
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-950">
                                    R$
                                  </span>
                                </div>
                              ) : (
                                <div className="text-zinc-950 text-sm px-2">
                                  {`${formatCurrency(installment.amount)} (Setup fee)`}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}

            <div className="self-stretch flex-col justify-start items-start gap-2.5 flex">
              <div className="self-stretch h-px border border-[#e3e3e7]" />
            </div>

            <div className="self-stretch flex-col justify-start items-start gap-px flex">
              <div className="self-stretch text-black text-lg font-medium font-['Inter'] leading-7">
                Outras Informações
              </div>
              <div className="self-stretch text-zinc-500 text-sm font-normal font-['Inter'] leading-tight">
                Informações adicionais do pacote.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 w-full">
              <Input
                label="Deal ID"
                className="col-span-1"
                showlabel
                placeholder="Digite o Deal ID"
                {...getInputProps(fields.dealId, { type: "text" })}
                error={fields.dealId.errors}
              />
            </div>

            <div className="flex flex-col">
              <p className="text-red-500 text-sm">
                {fields.installmentsData.errors}
              </p>
              {installmentsData.some(
                (_, index) =>
                  (fields as any)[`installmentsDates[${index}]`]?.errors,
              ) && (
                <p className="text-red-500 text-sm">
                  Preencha todas as datas das parcelas
                </p>
              )}
              {installmentsData.some(
                (_, index) =>
                  (fields as any)[`installmentsAmounts[${index}]`]?.errors,
              ) && (
                <p className="text-red-500 text-sm">
                  Preencha todos os valores das parcelas
                </p>
              )}
            </div>
          </div>

          <div className="self-stretch flex-col justify-start items-start gap-2 flex">
            <div className="self-stretch h-px border border-[#e3e3e7]" />
          </div>

          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="px-4 py-2"
            >
              Cancelar
            </Button>
            <Button type="submit" className="px-4 py-2" disabled={isSubmitting}>
              Criar license fee
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
